import { useEffect, useMemo, useState } from 'react';
import { BroadcastEventType } from '@cotera/api';
import { subscribe } from '@cotera/client/app/etc/broadcast-event-bus';
import { isDatasetResourceEvent } from '@cotera/client/app/v1/watchables/use-resource-updated-watchable';
import type { ResourceUpdatedFilterPayload } from '@cotera/client/app/v1/watchables/use-resource-updated-watchable';
import {
  DatasetLiveOperations,
  type LiveOperationsClient,
  type LiveOperationsTable,
} from './live-operations';
import type { DatasetPreviewColumn } from './operations';
import type { DatasetPreviewLayer } from './types';

/**
 * Wires {@link DatasetLiveOperations} to the event bus for as long as a grid is
 * mounted.
 *
 * The listener is `ResourceUpdated` rather than anything operation-shaped,
 * because that is what the server sends. It fires for renames, row-key changes
 * and the grid's *own* edits as well, so every one of them would be a reason to
 * re-read the log — which is why the sync asks for operations *after* the last
 * one applied. That question answers "is there anything here I have not already
 * got?" in one cheap call, and answers it "no" for the noisy majority.
 */
export function useLiveDatasetOperations(params: {
  datasetId: string | null;
  /** The artifact the preview is keyed on; null before there is one. */
  artifactId: string | null;
  /**
   * The materialized preview table, or null until its initial replay has
   * finished. Both states are ordinary: the grid mounts long before the parquet
   * has been read.
   */
  table: LiveOperationsTable | null;
  /** The schema the preview table has now, after its initial replay. */
  columns: readonly DatasetPreviewColumn[];
  rowIdColumn: string | null;
  client: LiveOperationsClient;
}): {
  /** Add to the grid's layer stack: this is how patches reach loaded rows. */
  readonly layer: DatasetPreviewLayer;
  /**
   * Changes only when an operation arrived that no patch can express — a column
   * added, renamed or dropped. Callers put it in the materialization's deps, so
   * a rebuild happens for those and for nothing else.
   */
  readonly schemaVersion: number;
  /** Records the high-water mark a from-scratch replay just applied. */
  readonly markApplied: (sequenceNumbers: readonly number[]) => void;
} {
  const { datasetId, artifactId, table, columns, rowIdColumn, client } = params;
  const [schemaVersion, setSchemaVersion] = useState(0);

  const live = useMemo(
    () =>
      new DatasetLiveOperations({
        client,
        // `setState` is stable, so this closure is too — which matters because
        // the object is what holds the layer identity the grid is memoized on.
        onSchemaChanged: () => setSchemaVersion((current) => current + 1),
      }),
    [client]
  );

  // In an effect rather than during render: this mutates the object, and a
  // render that React discards must not move the high-water mark with it.
  useEffect(() => {
    live.setSource(
      datasetId === null || artifactId === null
        ? null
        : { datasetId, artifactId, table, columns, rowIdColumn }
    );
  }, [artifactId, columns, datasetId, live, rowIdColumn, table]);

  useEffect(() => {
    if (datasetId === null) {
      return;
    }
    return subscribe(BroadcastEventType.ResourceUpdated, (payload: unknown) => {
      const event = payload as ResourceUpdatedFilterPayload & {
        resourceId?: string;
      };
      if (!isDatasetResourceEvent(event)) {
        return;
      }
      // A payload that does not say which dataset it is about still counts:
      // the sync's own high-water check is what makes an unnecessary one cheap,
      // and dropping an event that might be ours would leave a write invisible.
      if (event.resourceId !== undefined && event.resourceId !== datasetId) {
        return;
      }
      void live.sync();
    });
  }, [datasetId, live]);

  return { layer: live.layer, schemaVersion, markApplied: live.markApplied };
}
